/**
 * How the CRF Builder reads, edits and numbers option codes.
 *
 * THE RULE: the Builder never changes a code. What is stored comes back
 * unchanged, in its own type: a number stays that number (0, 1, 88, 98, 99),
 * text stays that text (DLM1, DL1, DL2, Y, N), and an option with no code
 * stays without one.
 *
 * WHY. Until 2026-09-18 the Builder turned every code that was not a number
 * into 0 when it loaded a form, and the next save wrote the 0 back. On the
 * 211At-MABG study (IoHVmxiejEuGXTS5Vlct, Clinical Dev) all six dose-level
 * fields, printed DLM1 / DL1 / DL2 on the CRF, are stored as 0 on every
 * option because of it. Guarded by crfOptionCodes.test.ts and, end to end
 * through the server save, functions/src/crf/crfBuilderOptionCodes.emulator.test.ts.
 */

/** An option as it is stored on a form (`artifacts/crf-*`, `content`). */
export interface StoredOption {
  label: string;
  value?: number | string | null;
}

/**
 * An option as the Builder holds it while a form is open. `code` is absent
 * when the option has none; the save sends `{ code, label }` and the server
 * stores `code` as `value` (saveCrfForm.ts), leaving it out when absent.
 */
export interface BuilderOption {
  code?: number | string;
  label: string;
}

/** A stored option as the Builder loads it: the code as stored, or none. */
export function storedOptionToBuilder(o: StoredOption): BuilderOption {
  return o.value === undefined || o.value === null
    ? { label: o.label }
    : { code: o.value, label: o.label };
}

/**
 * A stored option as the Builder reads it back after "add from a previous
 * study". The same reading as a load: there is one rule, not two.
 */
export const importedOptionToBuilder = storedOptionToBuilder;

/**
 * What the question editor's code box stores for what was typed. A whole
 * number is stored as a number, so typing 98 gives the number 98 that every
 * other form carries; anything else is stored as the text typed; an emptied
 * box means the option has no code.
 */
export function optionCodeFromInput(text: string): number | string | undefined {
  const t = text.trim();
  if (t === '') return undefined;
  return /^-?\d+$/.test(t) ? Number(t) : t;
}

/**
 * The code "Add option" gives a new option: one more than the highest
 * numeric code on the list. Text codes are left out of the arithmetic, so a
 * list of DLM1 / DL1 / DL2 suggests 1 rather than nothing usable.
 */
export function nextOptionCode(options: BuilderOption[], responseType: string): number {
  if (responseType === 'multi_select') return 1;
  const numeric = options
    .map((o) => o.code)
    .filter((c): c is number => typeof c === 'number' && Number.isFinite(c));
  return numeric.length > 0 ? Math.max(...numeric) + 1 : 1;
}
